import { Component, OnInit, ViewChild } from "@angular/core";
import { Table } from 'primeng/table';
import { BreadcrumbService } from '../../app.breadcrumb.service';
import { MenuItem } from 'primeng/api/menuitem';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Router, ActivatedRoute } from '@angular/router';
import { BaseComponent } from 'src/app/base/base-component';
import { DonneesBaseService } from 'src/app/services/donnees-base-service';
import { ProfilService } from 'src/app/services/profil-service';
import { AppMainComponent } from 'src/app/app.main.component';
import { CleApiService } from 'src/app/services/cle-api-service';
import { CleApiEntite } from 'src/app/entites/cle-api';
import { DetailCleApiComponent } from './detail-cle-api.component';

@Component({
    templateUrl: './cles-api.component.html',
    providers: [MessageService, ConfirmationService, DialogService]
})
export class ClesApiComponent extends BaseComponent implements OnInit {

    clesApi: CleApiEntite[] = [];
    selectionCle: CleApiEntite;
    menuContextuel: MenuItem[];
    dialogRef: DynamicDialogRef;
    today: Date = new Date();

    @ViewChild('dt') table: Table;

    constructor(
        sdb: DonneesBaseService,
        profilService: ProfilService,
        route: ActivatedRoute,
        appMain: AppMainComponent,
        private breadcrumbService: BreadcrumbService,
        private cleApiService: CleApiService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        public dialogService: DialogService,
        public routerService: Router) {
        super(sdb, profilService, route, routerService, appMain);
        
        this.breadcrumbService.setItems([
            { label: 'Administration', routerLink: '/admin' },
            { label: 'Clés API' }
        ]);
    }

    ngOnInit() {
        this.verifierAccesPage('admin_cles_api');
        this.chargerDonnees();
        this.initialiserMenuContextuel();
    }

    /**
     * Charge toutes les clés API du système
     */
    chargerDonnees() {
        this.cleApiService.obtenirClesApi().subscribe({
            next: (cles) => {
                this.clesApi = cles;
                this.CompleterChargement();
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Erreur lors du chargement des clés API'
                });
            }
        });
    }

    /**
     * Initialise le menu contextuel pour les lignes du tableau
     */
    initialiserMenuContextuel() {
        this.menuContextuel = [
            { 
                label: 'Modifier', 
                icon: 'pi pi-fw pi-pencil', 
                command: () => this.ouvrirDetail(this.selectionCle)
            },
            { 
                label: 'Régénérer la clé', 
                icon: 'pi pi-fw pi-refresh', 
                command: () => this.confirmerRegeneration(this.selectionCle)
            },
            { separator: true },
            { 
                label: 'Supprimer', 
                icon: 'pi pi-fw pi-trash',
                command: () => this.confirmerSuppression(this.selectionCle)
            }
        ];
    }

    /**
     * Ouvre le dialogue de détail/création d'une clé API
     */
    ouvrirDetail(cle?: CleApiEntite) {
        const isMobile = window.innerWidth < 768;
        this.dialogRef = this.dialogService.open(DetailCleApiComponent, {
            header: cle ? `Modifier la clé: ${cle.nom}` : 'Nouvelle clé API',
            width: isMobile ? '100vw' : '500px',
            data: {
                cle: cle
            }
        });

        this.dialogRef.onClose.subscribe((result) => {
            if (result) {
                this.chargerDonnees();
            }
        });
    }

    /**
     * Action pour ajouter une nouvelle clé
     */
    ajouterCle() {
        this.ouvrirDetail();
    }

    /**
     * Confirme la suppression d'une clé API
     */
    confirmerSuppression(cle: CleApiEntite) {
        this.confirmationService.confirm({
            message: `Êtes-vous sûr de vouloir supprimer la clé API "${cle.nom}" ?`,
            header: 'Confirmation de suppression',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.supprimerCle(cle);
            }
        });
    }

    /**
     * Confirme la régénération d'une clé API
     */
    confirmerRegeneration(cle: CleApiEntite) {
        this.confirmationService.confirm({
            message: `Êtes-vous sûr de vouloir régénérer la clé API "${cle.nom}" ? L'ancienne clé ne fonctionnera plus.`,
            header: 'Confirmation de régénération',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.regenererCle(cle);
            }
        });
    }

    /**
     * Régénère une clé API et affiche la nouvelle valeur
     */
    regenererCle(cle: CleApiEntite) {
        this.cleApiService.regenererCleApi(cle.cleApiId).subscribe({
            next: (cleRegeneree) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Succès',
                    detail: 'Clé API régénérée avec succès'
                });
                
                // Afficher la nouvelle clé dans un dialogue
                this.confirmationService.confirm({
                    message: `Nouvelle clé API générée. Copiez-la maintenant, elle ne sera plus accessible après la fermeture de cette fenêtre.\n\nClé: ${cleRegeneree.valeurDechiffree}`,
                    header: 'Nouvelle clé générée',
                    icon: 'pi pi-key',
                    rejectVisible: false,
                    acceptLabel: 'Copier et fermer',
                    accept: () => {
                        // Copier dans le presse-papier
                        navigator.clipboard.writeText(cleRegeneree.valeurDechiffree).then(() => {
                            this.messageService.add({
                                severity: 'info',
                                summary: 'Copié',
                                detail: 'Clé copiée dans le presse-papier'
                            });
                        });
                    }
                });
                
                this.chargerDonnees();
            }
        });
    }

    /**
     * Supprime une clé API
     */
    supprimerCle(cle: CleApiEntite) {
        this.cleApiService.supprimerCleApi(cle.cleApiId).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Succès',
                    detail: 'Clé API supprimée avec succès'
                });
                this.chargerDonnees();
            }
        });
    }

    /**
     * Copie le Client ID dans le presse-papier
     */
    copierClientId(clientId: string) {
        navigator.clipboard.writeText(clientId);
        this.messageService.add({ severity: 'info', summary: 'Copié', detail: 'Client ID copié dans le presse-papier' });
    }
}

